import React, { useState, useEffect } from 'react';
import { fetchAllRegularUsers, deleteUser, verifyAdmin } from '../../services/adminService';
import { collection, onSnapshot, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAdminAuth } from '../../hooks/useAdminAuth';

function UserManagement({ onEditUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVip, setFilterVip] = useState('all');
  const [error, setError] = useState('');
  const [adminUids, setAdminUids] = useState(new Set());
  const { isAdmin } = useAdminAuth();

  // Helper function to filter out admin users
  const filterAdminUsers = async (usersList, currentAdminUids) => {
    try {
      let adminIds = currentAdminUids;
      
      // Get current admin UIDs if not already provided
      if (!adminIds || adminIds.size === 0) {
        const adminsRef = collection(db, 'adminUsers');
        const adminsSnapshot = await getDocs(adminsRef);
        adminIds = new Set();
        adminsSnapshot.forEach((doc) => {
          adminIds.add(doc.id);
        });
        setAdminUids(adminIds);
      }
      
      // Filter out admin users
      return usersList.filter(user => {
        // Check if user is in adminUsers collection
        if (adminIds.has(user.id)) {
          return false;
        }
        
        // Check if user has admin role flags
        if (user.role === 'admin' || user.role === 'superAdmin' || user.admin === true) {
          return false;
        }
        
        return true;
      });
    } catch (error) {
      console.error('Error filtering admin users:', error);
      // If filtering fails, return all users (better than showing nothing)
      return usersList;
    }
  };

  useEffect(() => {
    loadUsers();
    
    // Set up real-time listener for immediate updates
    const usersRef = collection(db, 'users');
    const unsubscribe = onSnapshot(
      usersRef,
      async (snapshot) => {
        const usersList = [];
        snapshot.forEach((doc) => {
          usersList.push({ id: doc.id, ...doc.data() });
        });
        
        // Filter out admin users from real-time updates
        const regularUsers = await filterAdminUsers(usersList, adminUids);
        setUsers(regularUsers);
        setLoading(false);
      },
      (error) => {
        console.error('Error in real-time listener:', error);
        // Fallback to regular fetch if listener fails
        loadUsers();
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Verify admin access before loading
      const adminStatus = await verifyAdmin();
      if (!adminStatus.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }
      
      // Fetch only regular users (excluding admins)
      const regularUsers = await fetchAllRegularUsers();
      setUsers(regularUsers);
      
      // Also refresh admin UIDs for real-time filtering
      const adminsRef = collection(db, 'adminUsers');
      const adminsSnapshot = await getDocs(adminsRef);
      const adminIds = new Set();
      adminsSnapshot.forEach((doc) => {
        adminIds.add(doc.id);
      });
      setAdminUids(adminIds);
    } catch (error) {
      console.error('Error loading users:', error);
      setError(error.message || 'Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesVip = filterVip === 'all' || user.vipLevel === filterVip;
    
    return matchesSearch && matchesVip;
  });

  const formatBalance = (balance) => {
    return balance !== undefined ? balance.toFixed(2) : '0.00';
  };

  const handleDeleteUser = async (userId, userEmail, username) => {
    // Verify admin access
    if (!isAdmin) {
      alert('Unauthorized: Admin access required');
      return;
    }

    const confirmMessage = `Are you sure you want to delete this user?\n\nEmail: ${userEmail}\nUsername: ${username}\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const reason = prompt('Please enter a reason for deleting this user (required):');
    
    if (!reason || reason.trim() === '') {
      alert('Reason is required for user deletion');
      return;
    }

    try {
      setError('');
      await deleteUser(userId, reason.trim());
      alert('User deleted successfully!');
      // Reload users list
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.message || 'Failed to delete user';
      setError(errorMessage);
      alert(`Failed to delete user: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show error if unauthorized
  if (!isAdmin) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Unauthorized: Admin access required
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-people me-2"></i>
          User Management
        </h2>
        <button className="btn btn-primary" onClick={loadUsers} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="search" className="form-label">Search Users</label>
              <input
                type="text"
                className="form-control"
                id="search"
                placeholder="Search by email, username, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="vipFilter" className="form-label">Filter by VIP Level</label>
              <select
                className="form-select"
                id="vipFilter"
                value={filterVip}
                onChange={(e) => setFilterVip(e.target.value)}
              >
                <option value="all">All Levels</option>
                <option value="VIP1">VIP1</option>
                <option value="VIP2">VIP2</option>
                <option value="VIP3">VIP3</option>
                <option value="VIP4">VIP4</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Username</th>
                  <th>VIP Level</th>
                  <th>Credit Score</th>
                  <th>Wallet Balance</th>
                  <th>Total Balance</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.email || 'N/A'}</td>
                      <td>{user.username || 'N/A'}</td>
                      <td>
                        <span className={`badge bg-${
                          user.vipLevel === 'VIP4' ? 'dark' :
                          user.vipLevel === 'VIP3' ? 'danger' :
                          user.vipLevel === 'VIP2' ? 'info' : 'warning'
                        }`}>
                          {user.vipLevel || 'N/A'}
                        </span>
                      </td>
                      <td>{user.creditScore ?? 'N/A'}</td>
                      <td>{formatBalance(user.balances?.wallet)} USDT</td>
                      <td>{formatBalance(user.balances?.total)} USDT</td>
                      <td>
                        {user.isDeleted ? (
                          <span className="badge bg-danger">Deleted</span>
                        ) : (
                          <span className={`badge bg-${user.isActive !== false ? 'success' : 'secondary'}`}>
                            {user.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => onEditUser(user.id)}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteUser(user.id, user.email, user.username)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-muted">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;

